'use client'

import { FC } from 'react'
import { Banner } from '@/root/ui/tuuleliinid/Banner/Banner'
import { Loader } from '@/root/ui/tuuleliinid'
import { PriceCardList } from '@/root/modules/shared/SelectTicketPage'
import { useGetTranslations } from '@/root/shared-hooks'
import { useTicketEditTabState } from '@/root/modules/shared/EditReservation/hooks/useTicketEditTabState'
import { CountryItem, Locale } from '@/root/shared-types'
import { PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'
import { ReservationWithModification } from '@/root/modules/shared/EditReservation/types'

const UNLIMITED_QTY = 999

interface EditReservationTicketsTabProps {
  reservation: ReservationWithModification
  countries: CountryItem[]
  sailPackageSeqN: number
  sailRefId?: number
  sailPackageCode: string
  sailRefIds: number[]
  reservationToken: string
  locale: Locale
  disabled?: boolean
  isSailCancelled?: boolean
  onTicketSaved: (updated: ReservationWithModification) => void
  onPendingChange?: (pending: boolean) => void
  onUnsavedFormsChange?: (hasUnsaved: boolean) => void
  onError?: (message: string, code?: string) => void
  onErrorClear?: () => void
  onSavePassengerInfo: (params: { priceCode: string; slotIndex: number; data: PassengerFormData }) => Promise<void>
}

export const EditReservationTicketsTab: FC<EditReservationTicketsTabProps> = ({
  reservation,
  countries,
  sailPackageSeqN,
  sailRefId,
  sailPackageCode,
  sailRefIds,
  reservationToken,
  locale,
  disabled = false,
  isSailCancelled = false,
  onTicketSaved,
  onPendingChange,
  onUnsavedFormsChange,
  onError,
  onErrorClear,
  onSavePassengerInfo,
}) => {
  const { getTranslation } = useGetTranslations()
  const s = useTicketEditTabState({
    getTranslation,
    reservation,
    sailPackageSeqN,
    sailRefId,
    sailPackageCode,
    sailRefIds,
    reservationToken,
    locale,
    disabled,
    onTicketSaved,
    onPendingChange,
    onUnsavedFormsChange,
    onError,
    onErrorClear,
  })

  if (!sailRefId) {
    return (
      <Banner
        variant="warning"
        className="w-full"
        body={getTranslation('commonNotificationErrorDesc')}
        title={getTranslation('bookingProcessErrorTitle')}
      />
    )
  }

  if (s.isLoading && !s.prices) {
    return (
      <div className="flex justify-center py-8">
        <Loader />
      </div>
    )
  }

  if (s.error || !s.prices) {
    return (
      <Banner
        variant="error"
        className="w-full"
        body={getTranslation('commonNotificationErrorDesc')}
        title={getTranslation('bookingProcessErrorTitle')}
      />
    )
  }

  const content = (
    <div className="flex w-full flex-col gap-5">
      <PriceCardList
        prices={s.prices.prices}
        countries={countries}
        priceFilter={(price) => price.priceCategory.isResident}
        maxTicketQty={UNLIMITED_QTY}
        disabled={s.isDisabled}
        route={s.currentRoute}
        departureDate={s.currentDepartureDate}
        initialQuantityByCode={s.displayInitialQuantityByCode}
        initialPassengersByCode={s.displayInitialPassengersByCode}
        hidePassengerDataForm={s.hidePassengerDataForm}
        onResidentIdVerified={s.handleResidentIdVerified}
        onPassengerSaved={async (priceCode, slotIndex, data) => onSavePassengerInfo({ priceCode, slotIndex, data })}
        onPassengerDeleted={s.handlePassengerDeleted}
        onFormStateChange={s.handleFormStateChange}
      />

      <PriceCardList
        prices={s.prices.prices}
        countries={countries}
        priceFilter={(price) => !price.priceCategory.isResident}
        maxTicketQty={UNLIMITED_QTY}
        disabled={s.isDisabled}
        initialQuantityByCode={s.initialQuantityByCode}
        initialPassengersByCode={s.initialPassengersByCode}
        hidePassengerDataForm={s.hidePassengerDataForm}
        onInc={async (price) => s.adjustQuantity(price.priceCategory.code, 1)}
        onDec={async (price) => s.adjustQuantity(price.priceCategory.code, -1)}
        onPassengerSaved={async (priceCode, slotIndex, data) => onSavePassengerInfo({ priceCode, slotIndex, data })}
        onPassengerDeleted={s.handlePassengerDeleted}
        onSetQuantity={s.setQuantity}
        onFormStateChange={s.handleFormStateChange}
      />

      <PriceCardList
        prices={s.prices.carDeckPrices ?? []}
        maxTicketQty={UNLIMITED_QTY}
        disabled={s.isDisabled}
        initialQuantityByCode={s.initialQuantityByCode}
        hidePassengerDataForm={s.hidePassengerDataForm}
        onInc={async (price) => s.adjustQuantity(price.priceCategory.code, 1)}
        onDec={async (price) => s.adjustQuantity(price.priceCategory.code, -1)}
        onPassengerDeleted={s.handlePassengerDeleted}
        onSetQuantity={s.setQuantity}
      />
    </div>
  )

  if (isSailCancelled) {
    return (
      <div className="relative">
        <div className="pointer-events-none opacity-50">{content}</div>
        <div className="absolute inset-0 cursor-not-allowed" onClick={s.handleCancelledOverlayClick} />
      </div>
    )
  }

  return content
}
